import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import useScrollIdle from '../../hooks/useScrollIdle';
import { LANGUAGES, whatsappHref } from '../../siteConfig';


function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35zM12.04 21.5h-.01a9.45 9.45 0 0 1-4.82-1.32l-.35-.21-3.58.94.96-3.49-.23-.36a9.43 9.43 0 0 1-1.45-5.03c0-5.22 4.25-9.47 9.48-9.47a9.4 9.4 0 0 1 6.7 2.78 9.41 9.41 0 0 1 2.77 6.7c0 5.22-4.25 9.46-9.47 9.46zm8.06-17.53A11.33 11.33 0 0 0 12.04.63C5.76.63.66 5.73.66 12.01c0 2 .52 3.96 1.52 5.69L.57 23.6l6.03-1.58a11.36 11.36 0 0 0 5.43 1.38h.01c6.27 0 11.38-5.1 11.38-11.38 0-3.04-1.19-5.9-3.33-8.05z" />
    </svg>
  );
}

// Fixed bottom bar: language switcher (left), "Book Now" pill (centre) and
// WhatsApp (right). Everything fades and drops slightly while the page is
// scrolling and comes back once it settles; focus or an open popover keeps
// them visible, and they are never removed from the page or the tab order.
function FloatingWidgets() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const scrolling = useScrollIdle(300);
  const [langOpen, setLangOpen] = useState(false);
  const [pastForm, setPastForm] = useState(false);
  const [hasHero, setHasHero] = useState(false);
  const langRef = useRef(null);
  const markerRef = useRef(null);

  const current = LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) || LANGUAGES[0];

  // The pill shows once the booking form has scrolled out above the viewport.
  // Pages without the form show it once a marker 400px down has scrolled past.
  useEffect(() => {
    setPastForm(false);
    setHasHero(Boolean(document.getElementById('hero')));
    const target = document.getElementById('book') || markerRef.current;
    if (!target) return undefined;
    // Huge bottom margin: 'intersecting' means 'not yet above the top edge'.
    const observer = new IntersectionObserver(([entry]) => setPastForm(!entry.isIntersecting), {
      rootMargin: '0px 0px 100000px 0px',
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [pathname]);

  useEffect(() => {
    if (!langOpen) return undefined;
    const onPointer = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setLangOpen(false);
        langRef.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  const goToForm = () => {
    const form = document.getElementById('book');
    if (form) {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      form.querySelector('input, button')?.focus({ preventScroll: true });
    } else {
      navigate('/#book');
    }
  };

  const hidden = scrolling && !langOpen;
  // Over the hero the corner buttons would cover the booking card and its
  // call bar (the hero has its own call/book options), so they wait below it.
  const corners = !hasHero || pastForm;
  const cornerClass = corners ? 'pointer-events-auto' : 'pointer-events-none !opacity-0';
  const cornerProps = corners ? {} : { inert: '', 'aria-hidden': true };
  const fade = `transition-[opacity,transform] duration-[250ms] ease-out motion-reduce:transition-none ${
    hidden
      ? 'translate-y-2 opacity-0 focus-within:translate-y-0 focus-within:opacity-100 motion-reduce:translate-y-0 motion-reduce:opacity-100'
      : 'translate-y-0 opacity-100'
  }`;

  return (
    <>
    <div ref={markerRef} aria-hidden="true" className="pointer-events-none absolute left-0 top-[400px] h-px w-px" />
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-widget font-sans">
      <div className="relative flex items-end justify-between px-4 pb-4 sm:px-6 sm:pb-6">
        <div ref={langRef} {...cornerProps} className={`relative ${cornerClass} ${fade}`}>
          <button
            type="button"
            className="focus-ring-light flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border-2 border-solid border-white/20 bg-navy text-sm font-extrabold tracking-wide text-white shadow-lg transition-colors hover:bg-navy-deep"
            aria-label={t('site.widgets.language', { language: current.name })}
            aria-expanded={langOpen}
            aria-controls="language-popover"
            onClick={() => setLangOpen((o) => !o)}
          >
            {current.code.toUpperCase()}
          </button>
          {langOpen && (
            <div
              id="language-popover"
              className="absolute bottom-full left-0 z-popover mb-3 w-48 overflow-hidden rounded-card bg-white p-2 shadow-card"
            >
              <ul className="m-0 list-none p-0">
                {LANGUAGES.map((lang) => (
                  <li key={lang.code}>
                    <button
                      type="button"
                      lang={lang.code}
                      aria-pressed={lang.code === current.code}
                      className="focus-ring-light flex min-h-[44px] w-full cursor-pointer items-center justify-between rounded-field border-0 bg-transparent px-3 text-left text-base font-semibold text-navy hover:bg-mist aria-pressed:bg-mist"
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                    >
                      {lang.name}
                      {lang.code === current.code && <Check size={18} aria-hidden="true" />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center sm:bottom-6">
          <button
            type="button"
            {...(pastForm ? {} : { inert: '', 'aria-hidden': true })}
            onClick={goToForm}
            className={`btn-primary focus-ring-light rounded-full px-7 shadow-lg transition-[opacity,transform] duration-[250ms] ease-out motion-reduce:transition-none ${
              pastForm && !hidden
                ? 'pointer-events-auto translate-y-0 opacity-100'
                : `translate-y-2 opacity-0 motion-reduce:translate-y-0 ${pastForm ? 'pointer-events-auto focus:translate-y-0 focus:opacity-100' : 'pointer-events-none'}`
            }`}
          >
            {t('site.nav.bookNow')}
          </button>
        </div>

        <a
          href={whatsappHref(t('site.widgets.whatsappMessage'))}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('site.widgets.whatsapp')}
          {...cornerProps}
          className={`focus-ring-light ${cornerClass} flex h-14 w-14 items-center justify-center rounded-full bg-whatsapp text-white shadow-lg hover:bg-[#1EBE5A] active:scale-[0.97] ${fade}`}
        >
          <WhatsAppIcon />
        </a>
      </div>
    </div>
    </>
  );
}

export default FloatingWidgets;
